/**
 * Coquille de l'application connectée (composition des écrans du design) :
 *   div.ag-screen.ag-canvas > a.agx-evitement (lien d'évitement) + div.ag-desk > Sidebar + main.ag-main#contenu
 *   (AppHeader, volets des pages ouvertes) + div.ag-dock-wrap > div.ag-dock-anchor > Dock
 * UNE barre latérale, UN en-tête, UN dock, quelle que soit la disposition (une page ou deux côte à côte) : ils
 * reflètent la page active et agissent sur elle (voir pages/espace.tsx). L'en-tête flotte au-dessus des volets : la page
 * défile dessous comme sous le dock (app.css › En-tête flottant).
 * Accessibilité (core/accessibilite.css) : « Aller au contenu » est le premier élément focalisable et amène le focus
 * sur la page active (après la barre latérale et l'en-tête) ; l'élément qui a le focus reste au-dessus du dock et sous
 * l'en-tête, entier s'il tient dans la page (zone de texte multiligne comprise : ./focus-visible.ts, Issue #295).
 * Fournisseurs de la coquille : softphone (téléphone du dock) et Scribe (enregistreur de réunions) – un appel ou un
 * enregistrement continue quand on change d'écran ou de page ; pendant un enregistrement, le titre de l'onglet commence
 * par « ● ».
 */
import React from 'react';
import { ajouterRecent } from '@agora/shared';
import { SkipLink, StatusBadge, cx } from '../../design';
import { useT } from '../i18n';
import { useSocle } from '../contexte';
import { BarreLaterale } from './BarreLaterale';
import { Entete } from './Entete';
import { DockAgora } from './DockAgora';
import { Recherche } from './Recherche';
import { useFocusAuDessusDuDock } from './focus-visible';
import { decrirePage } from './page';
import { useLargeur } from './utilitaires';
import { cheminSeul, useEtatEspace } from '../pages/espace';
import { Volets } from '../pages/Volets';
import { Softphone } from '../telephonie/softphone/Softphone';
import { FournisseurScribe, PREFIXE_ENREGISTREMENT, useScribe } from '../scribe/Scribe';

/** Identifiant du contenu principal (`<main>`), cible du lien d'évitement. */
export const ID_CONTENU = 'contenu';

export function Coquille() {
  const { t, lib, langue } = useT();
  const { config, preferences, modifierPreferences } = useSocle();
  const { routeActive, pageActive, annonce } = useEtatEspace();
  const chemin = cheminSeul(routeActive);
  const largeur = useLargeur();
  const [recherche, setRecherche] = React.useState(false);
  const [deplieTemporaire, setDeplieTemporaire] = React.useState(false);
  // Élément qui reçoit le focus (clavier, script) ramené entier au-dessus du dock et sous l'en-tête (WCAG 2.4.11).
  const focusVisible = useFocusAuDessusDuDock();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const page = React.useMemo(() => decrirePage(chemin, t, lib, pageActive.titre), [chemin, langue, pageActive.titre]);

  // Récents : chaque visite d'un module, sous-module ou référentiel alimente « Utilisés récemment ».
  const idNoeud = page.noeud?.id;
  const typeNoeud = page.noeud?.type;
  React.useEffect(() => {
    if (!idNoeud || typeNoeud === 'Metier') return;
    if (preferences.recents[0]?.id === idNoeud) return;
    modifierPreferences((p) => ajouterRecent(p, idNoeud));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idNoeud]);

  // Ctrl/⌘ K : recherche globale.
  React.useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setRecherche((r) => !r); }
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, []);

  const titreOnglet = page.titre && chemin !== '/' ? `${page.titre} · AGORA` : 'AGORA';

  // Barre latérale : repliée par défaut sous 1280 px (charte), sinon selon la préférence de l'utilisateur.
  const etroit = largeur < 1280;
  const replie = etroit ? !deplieTemporaire : preferences.barreLateraleRepliee;
  const basculer = () => {
    if (etroit) setDeplieTemporaire((d) => !d);
    else modifierPreferences((p) => ({ ...p, barreLateraleRepliee: !p.barreLateraleRepliee }));
  };

  // Cible du lien d'évitement : le volet de la page active (focalisable, nommé par le titre de la page).
  const pageAffichee = () => Array.from(document.querySelectorAll<HTMLElement>(`#${ID_CONTENU} [data-page-id]`))
    .find((el) => el.dataset.pageId === pageActive.id) ?? null;

  return (
    <div className={cx('ag-screen', 'ag-canvas', 'agx-app')} {...focusVisible}>
      {/* Softphone (pilote Amazon Connect) : panneau en bas à droite, numéros d'AGORA composés dans la page. */}
      <Softphone>
        <FournisseurScribe>
          <TitreDocument titre={titreOnglet} />
          <SkipLink label={t('nav.allerAuContenu')} targetId={ID_CONTENU} getTarget={pageAffichee} />
          <div className="ag-desk">
            <BarreLaterale actif={page.noeud} chemin={chemin} replie={replie} onBasculer={basculer} />
            <main className="ag-main" id={ID_CONTENU}>
              <Entete fil={page.fil} onRecherche={() => setRecherche(true)} />
              <Volets />
            </main>
          </div>
          <DockAgora idNoeudActif={idNoeud} accueilActif={chemin === '/'} titrePage={page.titre} compact={largeur < 640} />
          <div className="agx-annonce" role="status" aria-live="polite">{annonce}</div>
          {recherche ? <Recherche fermer={() => setRecherche(false)} /> : null}
          {config.maquette ? <div className="agx-maquette"><StatusBadge status="info" label={t('app.maquette')} /></div> : null}
          {config.env !== 'prod' && !config.maquette ? <div className="agx-maquette"><StatusBadge status="neutral" label={t('app.environnement', { env: config.env })} /></div> : null}
        </FournisseurScribe>
      </Softphone>
    </div>
  );
}

/** Titre de l'onglet du navigateur : celui de la page, précédé de « ● » pendant un enregistrement Scribe. */
function TitreDocument({ titre }: { titre: string }) {
  const enregistre = useScribe()?.etat.phase === 'enregistrement';
  React.useEffect(() => { document.title = `${enregistre ? PREFIXE_ENREGISTREMENT : ''}${titre}`; }, [titre, enregistre]);
  return null;
}
